import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Pause, Play, Star } from "lucide-react";
import { GALLERY, featuredArtist } from "@/data/gallery";
import { useGalleryStories } from "@/lib/galleryStories";
import { GalleryLightbox } from "@/components/GalleryLightbox";

const SPEED_KEY = "jt-reel-speed";
const SPEEDS = { slow: 420, normal: 280, fast: 170 };

const FeaturedArtist = () => {
  const f = featuredArtist();
  const stories = useGalleryStories();
  const [open, setOpen] = useState(null);
  const [storyFirst, setStoryFirst] = useState(false);
  const story = stories[f.src];
  return (
    <div className="mx-auto mb-5 max-w-7xl px-4 sm:px-8">
      <div className="flex items-center gap-4 rounded-3xl border border-primary/40 bg-primary/5 p-3 sm:gap-6 sm:p-4" data-testid="featured-artist">
        <button type="button" onClick={() => { setStoryFirst(false); setOpen(0); }} className="shrink-0 overflow-hidden rounded-2xl border-2 border-primary" aria-label={`View photo with ${f.name}`} data-testid="featured-artist-photo">
          <img src={f.src} alt={f.caption} className="h-28 w-24 object-cover transition-transform duration-500 hover:scale-105 sm:h-36 sm:w-28" />
        </button>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-primary"><Star size={12} className="fill-primary" /> Featured artist this week</p>
          <p className="mt-1 font-serif text-xl font-bold uppercase tracking-wide sm:text-2xl" data-testid="featured-artist-name">{f.name}{f.sub && <span className="block text-base sm:text-lg">{f.sub}</span>}</p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="featured-artist-caption">{f.caption}. A new face from Jason's journey every Monday.</p>
          {story && <button type="button" onClick={() => { setStoryFirst(true); setOpen(0); }} data-testid="featured-artist-story-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground"><BookOpen size={12} /> Tap to read story</button>}
        </div>
      </div>
      <GalleryLightbox items={[f]} stories={stories} storyFirst={storyFirst} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />
    </div>
  );
};

export const PhotoStrip = () => {
  const f = featuredArtist();
  const start = GALLERY.indexOf(f);
  const ordered = [...GALLERY.slice(start), ...GALLERY.slice(0, start)];
  const loop = [...ordered, ...ordered];
  const [paused, setPaused] = useState(false);
  const [speed, setSpeedState] = useState(() => { try { return localStorage.getItem(SPEED_KEY) || "normal"; } catch { return "normal"; } });
  const setSpeed = (s) => { setSpeedState(s); try { localStorage.setItem(SPEED_KEY, s); } catch { /* storage blocked */ } };
  const [held, setHeld] = useState(false);
  const touchAt = useRef(0);
  return (
    <section className="border-b border-border py-10" data-testid="home-photo-strip">
      <div className="mx-auto mb-5 flex max-w-7xl flex-col gap-3 px-4 sm:flex-row sm:items-end sm:justify-between sm:gap-4 sm:px-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-primary">As seen with…</p>
          <h2 className="mt-1 font-serif text-lg md:text-xl">On stage and back stage with some of music and TV's biggest names.</h2>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end sm:gap-4">
          <div className="flex items-center rounded-full border border-border p-0.5" role="group" aria-label="Reel speed" data-testid="photo-strip-speed">
            {Object.keys(SPEEDS).map((s) => (
              <button key={s} type="button" onClick={() => setSpeed(s)} aria-pressed={speed === s} data-testid={`photo-strip-speed-${s}`}
                className={`rounded-full px-2.5 py-1 text-xs capitalize transition-colors ${speed === s ? "bg-primary font-semibold text-primary-foreground" : "text-muted-foreground hover:text-primary"}`}>{s}</button>
            ))}
          </div>
          <button type="button" onClick={() => setPaused((p) => !p)} aria-pressed={paused} data-testid="photo-strip-pause"
            className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
            {paused ? <Play size={13} /> : <Pause size={13} />} {paused ? "Play reel" : "Pause reel"}
          </button>
          <Link to="/about#gallery" className="group hidden items-center gap-1.5 text-sm text-primary sm:flex" data-testid="photo-strip-gallery-link">
            View the gallery <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
      <FeaturedArtist />
      <div className={`photo-strip-mask overflow-hidden ${paused || held ? "is-paused" : ""}`} onTouchStart={() => { touchAt.current = Date.now(); setHeld(true); }} onTouchEnd={() => setHeld(false)} onTouchCancel={() => setHeld(false)} data-testid="photo-strip-mask">
        <Link to="/about#gallery" className="photo-strip-track flex w-max" style={{ animationDuration: `${SPEEDS[speed] || SPEEDS.normal}s` }} aria-label="Open the gallery" data-testid="photo-strip-track"
          onClick={(e) => { if (Date.now() - touchAt.current < 800) { e.preventDefault(); setPaused((p) => !p); } }}>
          {loop.map((g, i) => (
            <figure key={i} className={`relative mr-4 h-44 w-36 shrink-0 overflow-hidden rounded-2xl border sm:h-52 sm:w-40 ${i % ordered.length === 0 ? "border-2 border-primary" : "border-border"}`}>
              {i % ordered.length === 0 && <span className="absolute left-2 top-2 z-10 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase text-primary-foreground" data-testid={`photo-strip-featured-${i}`}>Featured</span>}
              <img src={g.src} alt={g.caption} loading="lazy" className="h-full w-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-2 pt-6 text-center text-xs font-extrabold uppercase tracking-wide text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]" data-testid={`photo-strip-name-${i}`}>{g.name}{g.sub && <span className="block">{g.sub}</span>}</figcaption>
            </figure>
          ))}
        </Link>
      </div>
      <Link to="/about#gallery" className="mx-auto mt-4 flex w-fit items-center gap-1.5 text-sm text-primary sm:hidden" data-testid="photo-strip-gallery-link-mobile">
        View the gallery <ArrowRight size={15} />
      </Link>
    </section>
  );
};
